"use strict";
/* globals module */
/**
 * @module calendarEventsRoute
 * @description BaasicCalendarEventsRoute Definition provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use BaasicCalendarEventsRoute Definition to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
var common_1 = require("../../../../common");
;
var contracts_1 = require("../../../../core/contracts");
var CalendarEventsRoute = /** @class */ (function (_super) {
    tslib_1.__extends(CalendarEventsRoute, _super);
    function CalendarEventsRoute(appOptions) {
        var _this = _super.call(this, appOptions) || this;
        _this.appOptions = appOptions;
        _this.findRoute = 'calendars/{calendarId}/events/{?searchQuery,page,rpp,sort,embed,fields,ids,ownerIds,statusIds,typeIds,from,to}';
        _this.getRoute = 'calendars/{calendarId}/events/{id}/{?embed,fields}';
        _this.getByEmailOrFullNameRoute = 'calendars/{calendarId}/events/{id}/emails/{emailOrFullName}';
        _this.linkRoute = 'calendars/{calendarId}/events/{id}';
        _this.updateRoute = 'calendars/{calendarId}/events/{id}';
        _this.unlinkRoute = 'calendars/{calendarId}/events/{id}';
        return _this;
    }
    /**
     * Parses find route which can be expanded with additional GetCalendarEventOptions. Supported items are:
     * - `searchQuery` - A string referencing CalendarEvent properties using the phrase or BQL (Baasic Query Language) search.
     * - `page` - A value used to set the page number, i.e. to retrieve certain CalendarEvent subset from the storage.
     * - `rpp` - A value used to limit the size of result set per page.
     * - `sort` - A string used to set the CalendarEvent property to sort the result collection by.
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * - `ids` - CalendarEvent ids which uniquely identify CalendarEvent resources that need to be retrieved.
     * - `ownerIds` - Owner ids which uniquely identify event owners used for filtering.
     * - `statusIds` - CalendarEventStatus ids which uniquely idendify CalendarEventStatuses used for filtering.
     * - `typeIds` - CalendarEventType ids which uniquely identify CalendarEventTypes used for filtering.
     * - `from` - Fluent syntax for 'From' date. Used to limit the dataset to only use resources starting from this date
     * - `to` - Fluent syntax for 'To' date. Used to limit the dataset to only use resources ending to this date.
     * @method
     * @param calendarId Calendar identifier which uniquely identifies a calendar resource.
     * @param options Query resource GetCalendarEventsOptions object.
     * @example calendarEventsRoute.find(calendarId, {searchQuery: '<search-phrase>'});
     */
    CalendarEventsRoute.prototype.find = function (calendarId, options) {
        var opt;
        if (options) {
            opt = options;
            opt.to = this.getToDate(opt);
            opt.from = this.getFromDate(opt);
        }
        else {
            opt = {};
        }
        var params = this.modelMapper.findParams(opt);
        params.calendarId = calendarId;
        return _super.prototype.baseFind.call(this, this.findRoute, params);
    };
    /**
     * Parses get route which must be expanded with the identifier of the previously created CalendarEvent resource. This route can be expanded using additional GetRequestOptions. Supported items are:
     * - `embed` - Comma separated list of resources to be contained within the current representation.
     * @method
     * @param calendarId Calendar id which uniquely identifies Calendar resource.
     * @param id CalendarEvent id which uniquely identifies CalendarEvent resource that needs to be retrieved.
     * @param options Query resource options object.
     * @example calendarEventsRoute.get(id);
     */
    CalendarEventsRoute.prototype.get = function (calendarId, id, options) {
        var params = this.modelMapper.getParams(options);
        params.calendarId = calendarId;
        return _super.prototype.baseGet.call(this, this.getRoute, id, params);
    };
    /**
     * Parses getByEmailOrFullName route which must be expanded with the identifier of the previously created CalendarEvent resource
     * The route must also be expanded with the identifier of the previously created CalendarResource and the email/FullName.
     * @param calendarId Calendar identifier which uniquely identifies previously created calendar resource.
     * @param id CalendarEvent identifier which uniquely identifies previously created CalendarEvent resource.
     * @param emailOrFullName Email or full name.
     * @param options Query resource options object.
     * @example calendarEventsRoute.get(calendarId, id, email@example.com, options);
     */
    CalendarEventsRoute.prototype.getByEmailOrFullName = function (calendarId, id, emailOrFullName, options) {
        var params = this.modelMapper.getParams(options);
        params.calendarId = calendarId;
        params.emailOrFullName = emailOrFullName;
        return _super.prototype.baseGet.call(this, this.getByEmailOrFullNameRoute, id, params);
    };
    /**
     * Parses link route. This URI template does not expose any additional options.
     * @method
     * @param data A calendarEvent resource to be linked.
     * @param calendarId A calendar identifier which uniquely identifies a calendar resource.
     * @example calendarEventsRoute.create(calendarid, eventid);
     */
    CalendarEventsRoute.prototype.link = function (calendarId, data) {
        var params = this.utility.extend({}, data);
        params.calendarId = calendarId;
        params.eventId = data.id;
        return _super.prototype.baseCreate.call(this, this.linkRoute, params);
    };
    /**
     * Parses update route. This URI template does not expose any additional options.
     * @method
     * @param data A CalendarEvent object used to update specified CalendarEvent resource.
     * @param calendarId A calendar identifier which uniquely identifies a calendar resourse.
     * @example calendarEventsRoute.update(calendarid, data);
     */
    CalendarEventsRoute.prototype.update = function (calendarId, data) {
        var params = this.utility.extend({}, data);
        params.calendarId = calendarId;
        return _super.prototype.baseUpdate.call(this, this.updateRoute, params);
    };
    /**
     * Parses unlink route. This URI template does not expose any additional options.
     * @method
     * @param data A calendarEvent resource to be unlinked.
     * @param calendarId A calendar identifier which uniquely identifies a calendar resourse.
     * @example calendarEventsRoute.unlink(calendarid, eventid);
     */
    CalendarEventsRoute.prototype.unlink = function (calendarId, data) {
        var params = this.utility.extend({}, data);
        params.calendarId = calendarId;
        params.eventId = data.id;
        return _super.prototype.baseDelete.call(this, this.unlinkRoute, params);
    };
    CalendarEventsRoute.prototype.getToDate = function (options) {
        if (!this.utility.isUndefined(options.to) && options.to !== null) {
            return options.to.toISOString();
        }
        return undefined;
    };
    CalendarEventsRoute.prototype.getFromDate = function (options) {
        if (!this.utility.isUndefined(options.from) && options.from !== null) {
            return options.from.toISOString();
        }
        return undefined;
    };
    var _a;
    CalendarEventsRoute = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(contracts_1.TYPES.IAppOptions)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof contracts_1.IAppOptions !== "undefined" && contracts_1.IAppOptions) === "function" ? _a : Object])
    ], CalendarEventsRoute);
    return CalendarEventsRoute;
}(common_1.BaseRoute));
exports.CalendarEventsRoute = CalendarEventsRoute;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](https://github.com/Baasic/baasic-rest-api/wiki) for detailed information about available Baasic REST API end-points.
 - [URI Template](https://github.com/Baasic/uritemplate-js) syntax enables expanding the Baasic route templates to Baasic REST URIs providing it with an object that contains URI parameters.
 - All end-point objects are transformed by the associated route service.
 */ 
